import { useParams, Link } from "react-router-dom"
import { useState, useEffect } from "react"
import { Year, Group, Team, Game, Player, GroupCode } from "../../types"

interface TeamWithStats extends Team {
    statsByCode: { [key: number]: Stats };
    z: number;
    v: number;
    p: number;
    r: number;
    g: number;
    ig: number;
    b: number;
}

interface Stats {
    z: number;
    v: number;
    p: number;
    r: number;
    g: number;
    ig: number;
    b: number;
}

interface PlayerStats extends Player {
    goals: number;
    assists: number;
    team: number;
}

interface GoalkeeperStats extends Player {
    interventions: number;
    team: number;
}

interface RankingItem {
    place: number;
    team: Team | undefined;
}

interface RankingGroup {
    place: number;
    teams: RankingItem[];
    placeRange: string;
}

export default function UserHistoryYear() {
    const { id } = useParams<{ id: string }>()
    const [activeTab, setActiveTab] = useState<string>('vysledky')
    const [years, setYears] = useState<Year[]>([])
    const [groups, setGroups] = useState<Group[]>([])
    const [teams, setTeams] = useState<TeamWithStats[]>([])
    const [games, setGames] = useState<Game[]>([])
    const [players, setPlayers] = useState<PlayerStats[]>([])
    const [goalkeepers, setGoalkeepers] = useState<GoalkeeperStats[]>([])
    const [codes, setCodes] = useState<GroupCode[]>([])
    const [loading, setLoading] = useState<boolean>(true)

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                
                // Fetch all years
                const yearsRes = await fetch(`${process.env.REACT_APP_PATH}/api/year`)
                const yearsData: Year[] = await yearsRes.json()
                setYears(yearsData.sort((a, b) => b.year - a.year))

                // Fetch groups for this year
                const groupsRes = await fetch(`${process.env.REACT_APP_PATH}/api/group?year=${id}`)
                const groupsData: Group[] = await groupsRes.json()
                setGroups(groupsData)

                // Fetch group codes
                const codesRes = await fetch(`${process.env.REACT_APP_PATH}/api/group/codes?year=${id}`)
                const codesData: GroupCode[] = await codesRes.json()
                setCodes(codesData)

                // Fetch teams for this year
                const teamsRes = await fetch(`${process.env.REACT_APP_PATH}/api/teams?year=${id}`)
                const teamsData: Team[] = await teamsRes.json()

                // Fetch games for this year
                const gamesRes = await fetch(`${process.env.REACT_APP_PATH}/api/game?year=${id}`)
                const gamesData: Game[] = await gamesRes.json()
                setGames(gamesData.filter(g => g.status === 'finished'))

                // Calculate team standings with the games and codes
                calculateTeamStandings(teamsData, gamesData.filter(g => g.status === 'finished'), codesData)

                // Fetch players/stats for this year
                const playersRes = await fetch(`${process.env.REACT_APP_PATH}/api/player/stats?year=${id}`)
                const playersData = await playersRes.json()
                setPlayers(Array.isArray(playersData) ? playersData : [])

                // Fetch goalkeeper stats
                const gkRes = await fetch(`${process.env.REACT_APP_PATH}/api/player/keeper-stats?year=${id}`)
                const gkData = await gkRes.json()
                setGoalkeepers(Array.isArray(gkData) ? gkData : [])

                setLoading(false)
            } catch (error) {
                console.error('Error fetching data:', error)
                setLoading(false)
            }
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    // Calculate team standings
    const calculateTeamStandings = (teamsData: Team[], finishedGames: Game[], groupCodes: GroupCode[]) => {
        const teamsWithStats: TeamWithStats[] = teamsData.map(team => ({
            ...team,
            statsByCode: {},
            z: 0,
            v: 0,
            p: 0,
            r: 0,
            g: 0,
            ig: 0,
            b: 0
        }))

        finishedGames.filter(g => g.text === 'group').forEach(game => {
            let ta = teamsWithStats.find(t => t.ID === game.team_a_id)
            let tb = teamsWithStats.find(t => t.ID === game.team_b_id)

            if (!ta || !tb) return

            const taGroupIds = ta.group_codes 
                ? [...new Set(ta.group_codes.map(code => {
                    const gc = groupCodes.find(g => g.ID === code)
                    return gc ? gc.group_id : null
                }).filter(id => id !== null))] as number[]
                : []

            const tbGroupIds = tb.group_codes 
                ? [...new Set(tb.group_codes.map(code => {
                    const gc = groupCodes.find(g => g.ID === code)
                    return gc ? gc.group_id : null
                }).filter(id => id !== null))] as number[]
                : []

            const sharedGroupIds = taGroupIds.filter(groupId => tbGroupIds.includes(groupId))

            sharedGroupIds.forEach(parentGroupId => {
                const parentGroupCodeIds = groupCodes
                    .filter(gc => gc.group_id === parentGroupId)
                    .map(gc => gc.ID)

                const taCodesInGroup = ta!.group_codes 
                    ? ta!.group_codes.filter(code => parentGroupCodeIds.includes(code))
                    : []
                const tbCodesInGroup = tb!.group_codes 
                    ? tb!.group_codes.filter(code => parentGroupCodeIds.includes(code))
                    : []

                taCodesInGroup.forEach(taCode => {
                    if (!ta!.statsByCode[taCode]) {
                        ta!.statsByCode[taCode] = { z: 0, v: 0, p: 0, r: 0, g: 0, ig: 0, b: 0 }
                    }
                    ta!.statsByCode[taCode].z++
                    ta!.statsByCode[taCode].g += game.team_a_goals || 0
                    ta!.statsByCode[taCode].ig += game.team_b_goals || 0

                    if (game.winner === ta!.ID) {
                        ta!.statsByCode[taCode].v++
                        ta!.statsByCode[taCode].b += 2
                    } else if (game.winner === tb!.ID) {
                        ta!.statsByCode[taCode].p++
                    } else if (game.tie) {
                        ta!.statsByCode[taCode].r++
                        ta!.statsByCode[taCode].b += 1
                    }
                })

                tbCodesInGroup.forEach(tbCode => {
                    if (!tb!.statsByCode[tbCode]) {
                        tb!.statsByCode[tbCode] = { z: 0, v: 0, p: 0, r: 0, g: 0, ig: 0, b: 0 }
                    }
                    tb!.statsByCode[tbCode].z++
                    tb!.statsByCode[tbCode].g += game.team_b_goals || 0
                    tb!.statsByCode[tbCode].ig += game.team_a_goals || 0

                    if (game.winner === tb!.ID) {
                        tb!.statsByCode[tbCode].v++
                        tb!.statsByCode[tbCode].b += 2
                    } else if (game.winner === ta!.ID) {
                        tb!.statsByCode[tbCode].p++
                    } else if (game.tie) {
                        tb!.statsByCode[tbCode].r++
                        tb!.statsByCode[tbCode].b += 1
                    }
                })
            })
        })

        setTeams(teamsWithStats)
    }

    // Get teams in a group
    const getTeamsInGroup = (group: Group): TeamWithStats[] => {
        return teams.filter(team => {
            if (team.group_codes && Array.isArray(team.group_codes)) {
                return team.group_codes.some(codeId => {
                    const code = codes.find(z => z.ID === codeId)
                    return code && code.name.includes(group.group_name)
                })
            }
            return false
        }).map(team => {
            let relevantCodeIds: number[] = []
            if (team.group_codes && Array.isArray(team.group_codes)) {
                relevantCodeIds = team.group_codes.filter(codeId => {
                    const code = codes.find(z => z.ID === codeId)
                    return code && code.name.includes(group.group_name)
                })
            }

            const stats: Stats = { z: 0, v: 0, p: 0, r: 0, g: 0, ig: 0, b: 0 }
            relevantCodeIds.forEach(codeId => {
                const codeStats = team.statsByCode?.[codeId]
                if (codeStats) {
                    stats.z += codeStats.z
                    stats.v += codeStats.v
                    stats.p += codeStats.p
                    stats.r += codeStats.r
                    stats.g += codeStats.g
                    stats.ig += codeStats.ig
                    stats.b += codeStats.b
                }
            })
            
            return { ...team, ...stats }
        }).sort((a, b) => b.b - a.b || (b.g - b.ig) - (a.g - a.ig) || b.g - a.g)
    }

    // Calculate top scorers
    const getTopScorers = (): PlayerStats[] => {
        if (!Array.isArray(players) || players.length === 0) {
            return []
        }
        return players
            .filter(p => p.goals > 0)
            .sort((a, b) => b.goals - a.goals)
            .slice(0, 15)
    }

    // Get top goalkeepers
    const getTopGoalkeepers = (): GoalkeeperStats[] => {
        if (!Array.isArray(goalkeepers) || goalkeepers.length === 0) {
            return []
        }
        return goalkeepers
            .filter(gk => gk.interventions > 0)
            .sort((a, b) => b.interventions - a.interventions)
            .slice(0, 10)
    }

    // Get top assists
    const getTopAssists = (): PlayerStats[] => {
        if (!Array.isArray(players) || players.length === 0) {
            return []
        }
        return players
            .filter(p => p.assists > 0)
            .sort((a, b) => b.assists - a.assists)
            .slice(0, 10)
    }

    // Calculate final ranking from playoff games
    const getFinalRanking = (): RankingGroup[] => {
        const rankings: { [key: number]: RankingItem } = {}
        const playoffGames = games.filter(g => g.text && g.text !== 'group')

        playoffGames.forEach(game => {
            const text = game.text?.toLowerCase() || ''
            
            if (text === 'finale' || text === 'finále' || text === 'final') {
                if (game.winner) {
                    rankings[game.winner] = { place: 1, team: teams.find(t => t.ID === game.winner) }
                    const loser = game.winner === game.team_a_id ? game.team_b_id : game.team_a_id
                    rankings[loser] = { place: 2, team: teams.find(t => t.ID === loser) }
                } else if (game.tie) {
                    // Both teams share 1st-2nd place
                    rankings[game.team_a_id] = { place: 1, team: teams.find(t => t.ID === game.team_a_id) }
                    rankings[game.team_b_id] = { place: 1, team: teams.find(t => t.ID === game.team_b_id) }
                }
            } else if (text === 'o3' || text === 'o 3') {
                if (game.winner) {
                    rankings[game.winner] = { place: 3, team: teams.find(t => t.ID === game.winner) }
                    const loser = game.winner === game.team_a_id ? game.team_b_id : game.team_a_id
                    rankings[loser] = { place: 4, team: teams.find(t => t.ID === loser) }
                } else if (game.tie) {
                    rankings[game.team_a_id] = { place: 3, team: teams.find(t => t.ID === game.team_a_id) }
                    rankings[game.team_b_id] = { place: 3, team: teams.find(t => t.ID === game.team_b_id) }
                }
            } else if (text === 'o5' || text === 'o 5') {
                if (game.winner) {
                    rankings[game.winner] = { place: 5, team: teams.find(t => t.ID === game.winner) }
                    const loser = game.winner === game.team_a_id ? game.team_b_id : game.team_a_id
                    rankings[loser] = { place: 6, team: teams.find(t => t.ID === loser) }
                } else if (game.tie) {
                    rankings[game.team_a_id] = { place: 5, team: teams.find(t => t.ID === game.team_a_id) }
                    rankings[game.team_b_id] = { place: 5, team: teams.find(t => t.ID === game.team_b_id) }
                }
            } else if (text === 'o7' || text === 'o 7') {
                if (game.winner) {
                    rankings[game.winner] = { place: 7, team: teams.find(t => t.ID === game.winner) }
                    const loser = game.winner === game.team_a_id ? game.team_b_id : game.team_a_id
                    rankings[loser] = { place: 8, team: teams.find(t => t.ID === loser) }
                } else if (game.tie) {
                    rankings[game.team_a_id] = { place: 7, team: teams.find(t => t.ID === game.team_a_id) }
                    rankings[game.team_b_id] = { place: 7, team: teams.find(t => t.ID === game.team_b_id) }
                }
            } else if (text === 'o9' || text === 'o 9') {
                if (game.winner) {
                    rankings[game.winner] = { place: 9, team: teams.find(t => t.ID === game.winner) }
                    const loser = game.winner === game.team_a_id ? game.team_b_id : game.team_a_id
                    rankings[loser] = { place: 10, team: teams.find(t => t.ID === loser) }
                } else if (game.tie) {
                    rankings[game.team_a_id] = { place: 9, team: teams.find(t => t.ID === game.team_a_id) }
                    rankings[game.team_b_id] = { place: 9, team: teams.find(t => t.ID === game.team_b_id) }
                }
            } else if (text === 'semifinale' || text === 'semifinále' || text.match(/^sf\d+$/)) {
                // Semifinal losers get 3rd-4th place (will compete in o3 match)
                // Only record if they don't already have a better placement
                if (game.winner) {
                    const loser = game.winner === game.team_a_id ? game.team_b_id : game.team_a_id
                    if (!rankings[loser]) {
                        rankings[loser] = { place: 3, team: teams.find(t => t.ID === loser) }
                    }
                }
            } else if (text.match(/^qf\d+$/)) {
                // Quarterfinal losers get 5th-8th place range
                if (game.winner) {
                    const loser = game.winner === game.team_a_id ? game.team_b_id : game.team_a_id
                    if (!rankings[loser]) {
                        rankings[loser] = { place: 5, team: teams.find(t => t.ID === loser) }
                    }
                }
            }
        })

        // Convert to array and sort by place
        const rankingArray: RankingItem[] = Object.values(rankings).sort((a, b) => a.place - b.place)
        
        // Group by place (in case there are ties/ranges)
        const groupedRankings: RankingGroup[] = []
        let currentGroup: RankingItem[] = []
        let currentPlace: number | null = null
        
        rankingArray.forEach(rank => {
            if (currentPlace === null || currentPlace === rank.place) {
                currentGroup.push(rank)
                currentPlace = rank.place
            } else {
                // Determine place range based on number of teams at that place
                const maxPlace = currentPlace + currentGroup.length - 1
                groupedRankings.push({
                    place: currentPlace,
                    teams: currentGroup,
                    placeRange: currentGroup.length > 1 ? `${currentPlace}.–${maxPlace}.` : `${currentPlace}.`
                })
                currentGroup = [rank]
                currentPlace = rank.place
            }
        })
        
        if (currentGroup.length > 0 && currentPlace !== null) {
            const maxPlace = currentPlace + currentGroup.length - 1
            groupedRankings.push({
                place: currentPlace,
                teams: currentGroup,
                placeRange: currentGroup.length > 1 ? `${currentPlace}.–${maxPlace}.` : `${currentPlace}.`
            })
        }

        return groupedRankings
    }

    return (
        <>
            {/* Year Navigation Bar */}
            <div className="w-full bg-white border-b border-borders">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex justify-center items-center gap-8 py-4 flex-wrap">
                        {years.map(yearData => (
                            <Link 
                                key={yearData.year}
                                to={`/history/${yearData.year}`} 
                                className={`text-lg hover:text-accent transition-colors ${id === String(yearData.year) ? 'text-accent font-semibold' : ''}`}
                            >
                                {yearData.year}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {/* Sub Navigation Bar */}
            <div className="w-full bg-gray-100 border-b border-borders">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex justify-center items-center gap-8 py-3">
                        <button 
                            onClick={() => setActiveTab('vysledky')}
                            className={`text-base hover:text-accent transition-colors ${activeTab === 'vysledky' ? 'text-accent font-semibold' : ''}`}
                        >
                            Výsledky
                        </button>
                        <button 
                            onClick={() => setActiveTab('statistiky')}
                            className={`text-base hover:text-accent transition-colors ${activeTab === 'statistiky' ? 'text-accent font-semibold' : ''}`}
                        >
                            Štatistiky
                        </button>
                        <button 
                            onClick={() => setActiveTab('zapasy')}
                            className={`text-base hover:text-accent transition-colors ${activeTab === 'zapasy' ? 'text-accent font-semibold' : ''}`}
                        >
                            Zápasy
                        </button>
                        <button 
                            onClick={() => setActiveTab('timy')}
                            className={`text-base hover:text-accent transition-colors ${activeTab === 'timy' ? 'text-accent font-semibold' : ''}`}
                        >
                            Tímy
                        </button>
                    </div>
                </div>
            </div>

            <div className="w-[90vw] md:w-[70vw] xl:w-[60vw] mx-auto mt-12 mb-12">
                <Link to='/history' className="text-accent mb-4 inline-block"> &lt;- Návrat</Link>
                
                {loading ? (
                    <div className="text-center py-12">
                        <p>Načítavam...</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-10">
                        <h1 className="text-4xl font-semibold text-accent mb-6">História roka {id}</h1>
                        
                        <div className="flex gap-2 border-b border-gray-200 mb-10">
                            <button 
                                onClick={() => setActiveTab('vysledky')} 
                                className={`px-6 py-3 font-medium transition-all ${
                                    activeTab === 'vysledky' 
                                        ? 'text-accent border-b-2 border-accent' 
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Výsledky
                            </button>
                            <button 
                                onClick={() => setActiveTab('statistiky')} 
                                className={`px-6 py-3 font-medium transition-all ${
                                    activeTab === 'statistiky' 
                                        ? 'text-accent border-b-2 border-accent' 
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Štatistiky
                            </button>
                            <button 
                                onClick={() => setActiveTab('zapasy')} 
                                className={`px-6 py-3 font-medium transition-all ${
                                    activeTab === 'zapasy' 
                                        ? 'text-accent border-b-2 border-accent' 
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Zápasy
                            </button>
                            <button 
                                onClick={() => setActiveTab('timy')} 
                                className={`px-6 py-3 font-medium transition-all ${
                                    activeTab === 'timy' 
                                        ? 'text-accent border-b-2 border-accent' 
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Tímy
                            </button>
                        </div>
                        
                        {activeTab === 'vysledky' && (
                            <div>
                                {/* Tournament Overview Stats */}
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
                                    <div className="text-center">
                                        <div className="text-5xl font-bold text-accent mb-2">{teams.length}</div>
                                        <div className="text-sm uppercase tracking-wider text-gray-600">Tímov</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-5xl font-bold text-accent mb-2">{games.length}</div>
                                        <div className="text-sm uppercase tracking-wider text-gray-600">Zápasov</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-5xl font-bold text-accent mb-2">{games.reduce((sum, g) => sum + (g.team_a_goals || 0) + (g.team_b_goals || 0), 0)}</div>
                                        <div className="text-sm uppercase tracking-wider text-gray-600">Gólov</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-5xl font-bold text-accent mb-2">{games.length > 0 ? (games.reduce((sum, g) => sum + (g.team_a_goals || 0) + (g.team_b_goals || 0), 0) / games.length).toFixed(1) : 0}</div>
                                        <div className="text-sm uppercase tracking-wider text-gray-600">Gólov/zápas</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-5xl font-bold text-accent mb-2">{players.length}</div>
                                        <div className="text-sm uppercase tracking-wider text-gray-600">Hráčov</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-5xl font-bold text-accent mb-2">{groups.filter(g => g.group_name !== '-').length}</div>
                                        <div className="text-sm uppercase tracking-wider text-gray-600">Skupín</div>
                                    </div>
                                </div>

                                {/* Tournament Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                                    <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                                        <h4 className="font-semibold text-gray-800 mb-3">Skupinová fáza</h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Skupín:</span>
                                                <span className="font-semibold">{groups.filter(g => g.group_name !== '-').length}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Zápasov:</span>
                                                <span className="font-semibold">{games.filter(g => !g.text || g.text === 'group').length}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Gólov:</span>
                                                <span className="font-semibold">{games.filter(g => !g.text || g.text === 'group').reduce((sum, g) => sum + (g.team_a_goals || 0) + (g.team_b_goals || 0), 0)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-6 border border-purple-100">
                                        <h4 className="font-semibold text-gray-800 mb-3">Play-off</h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Zápasov:</span>
                                                <span className="font-semibold">{games.filter(g => g.text && g.text !== 'group').length}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Gólov:</span>
                                                <span className="font-semibold">{games.filter(g => g.text && g.text !== 'group').reduce((sum, g) => sum + (g.team_a_goals || 0) + (g.team_b_goals || 0), 0)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Tímov v play-off:</span>
                                                <span className="font-semibold">{getFinalRanking().length > 0 ? getFinalRanking().reduce((sum, g) => sum + g.teams.length, 0) : 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Final Ranking */}
                                {getFinalRanking().length > 0 && (
                                    <div className="mb-12">
                                        <h3 className="text-2xl font-semibold mb-6">Konečné poradie</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {getFinalRanking().map((ranking, i) => (
                                                <div key={i} className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-100">
                                                    <div className="flex items-center mb-4">
                                                        <div className="bg-yellow-400 text-white text-2xl font-bold rounded-full w-12 h-12 flex items-center justify-center mr-4">
                                                            {ranking.placeRange}
                                                        </div>
                                                        <div className="flex-1">
                                                            {ranking.teams.map((item, j) => (
                                                                <Link key={j} to={`/team/${item.team?.ID}`} className="block hover:text-accent transition-colors">
                                                                    <p className="text-lg font-semibold">{item.team?.name}</p>
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Group Standings */}
                                <div>
                                    <h3 className="text-2xl font-semibold mb-6">Tabuľky skupín</h3>
                                    <div className="space-y-8">
                                        {groups.filter(g => g.group_name !== '-').map((group, i) => {
                                            const groupTeams = getTeamsInGroup(group)
                                            return (
                                                <div key={i} className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                                                    <h4 className="text-xl font-semibold mb-4">{group.group_name}</h4>
                                                    {groupTeams.length > 0 ? (
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full text-sm">
                                                                <thead>
                                                                    <tr className="border-b-2 border-gray-200">
                                                                        <th className="text-left py-2 px-2">#</th>
                                                                        <th className="text-left py-2 px-2">Tím</th>
                                                                        <th className="text-center py-2 px-2">Z</th>
                                                                        <th className="text-center py-2 px-2">V</th>
                                                                        <th className="text-center py-2 px-2">R</th>
                                                                        <th className="text-center py-2 px-2">P</th>
                                                                        <th className="text-center py-2 px-2">G</th>
                                                                        <th className="text-center py-2 px-2">IG</th>
                                                                        <th className="text-center py-2 px-2">B</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {groupTeams.map((team, j) => (
                                                                        <tr key={j} className="border-b border-gray-100 hover:bg-gray-100">
                                                                            <td className="py-2 px-2">{j + 1}</td>
                                                                            <td className="py-2 px-2">
                                                                                <Link to={`/team/${team.ID}`} className="hover:text-accent transition-colors">
                                                                                    {team.name}
                                                                                </Link>
                                                                            </td>
                                                                            <td className="text-center py-2 px-2">{team.z}</td>
                                                                            <td className="text-center py-2 px-2">{team.v}</td>
                                                                            <td className="text-center py-2 px-2">{team.r}</td>
                                                                            <td className="text-center py-2 px-2">{team.p}</td>
                                                                            <td className="text-center py-2 px-2">{team.g}</td>
                                                                            <td className="text-center py-2 px-2">{team.ig}</td>
                                                                            <td className="text-center py-2 px-2 font-semibold">{team.b}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <p className="text-gray-500 text-center py-4">Žiadne tímy v tejto skupine</p>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'statistiky' && (
                            <div className="space-y-12">
                                {/* Top Scorers */}
                                <div>
                                    <h3 className="text-2xl font-semibold mb-6">Najlepší strelci</h3>
                                    {getTopScorers().length > 0 ? (
                                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b-2 border-gray-200">
                                                        <th className="text-left py-2 px-2">#</th>
                                                        <th className="text-left py-2 px-2">Hráč</th>
                                                        <th className="text-left py-2 px-2">Tím</th>
                                                        <th className="text-center py-2 px-2">Góly</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getTopScorers().map((player, i) => (
                                                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-100">
                                                            <td className="py-2 px-2">{i + 1}</td>
                                                            <td className="py-2 px-2 font-semibold">{player.first_name} {player.last_name}</td>
                                                            <td className="py-2 px-2">
                                                                <Link to={`/team/${player.team}`} className="hover:text-accent transition-colors">
                                                                    {teams.find(t => t.ID === player.team)?.name}
                                                                </Link>
                                                            </td>
                                                            <td className="text-center py-2 px-2 font-bold text-accent">{player.goals}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center py-4">Žiadne dáta</p>
                                    )}
                                </div>

                                {/* Top Assisters */}
                                <div>
                                    <h3 className="text-2xl font-semibold mb-6">Najlepší asistenti</h3>
                                    {getTopAssists().length > 0 ? (
                                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b-2 border-gray-200">
                                                        <th className="text-left py-2 px-2">#</th>
                                                        <th className="text-left py-2 px-2">Hráč</th>
                                                        <th className="text-left py-2 px-2">Tím</th>
                                                        <th className="text-center py-2 px-2">Asistencie</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getTopAssists().map((player, i) => (
                                                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-100">
                                                            <td className="py-2 px-2">{i + 1}</td>
                                                            <td className="py-2 px-2 font-semibold">{player.first_name} {player.last_name}</td>
                                                            <td className="py-2 px-2">
                                                                <Link to={`/team/${player.team}`} className="hover:text-accent transition-colors">
                                                                    {teams.find(t => t.ID === player.team)?.name}
                                                                </Link>
                                                            </td>
                                                            <td className="text-center py-2 px-2 font-bold text-accent">{player.assists}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center py-4">Žiadne dáta</p>
                                    )}
                                </div>

                                {/* Top Goalkeepers */}
                                <div>
                                    <h3 className="text-2xl font-semibold mb-6">Najlepší brankári</h3>
                                    {getTopGoalkeepers().length > 0 ? (
                                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b-2 border-gray-200">
                                                        <th className="text-left py-2 px-2">#</th>
                                                        <th className="text-left py-2 px-2">Brankár</th>
                                                        <th className="text-left py-2 px-2">Tím</th>
                                                        <th className="text-center py-2 px-2">Zákroky</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getTopGoalkeepers().map((gk, i) => (
                                                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-100">
                                                            <td className="py-2 px-2">{i + 1}</td>
                                                            <td className="py-2 px-2 font-semibold">{gk.first_name} {gk.last_name}</td>
                                                            <td className="py-2 px-2">
                                                                <Link to={`/team/${gk.team}`} className="hover:text-accent transition-colors">
                                                                    {teams.find(t => t.ID === gk.team)?.name}
                                                                </Link>
                                                            </td>
                                                            <td className="text-center py-2 px-2 font-bold text-accent">{gk.interventions}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center py-4">Žiadne dáta</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'zapasy' && (
                            <div>
                                <h3 className="text-2xl font-semibold mb-6">Všetky zápasy</h3>
                                {games.length > 0 ? (
                                    <div className="space-y-3">
                                        {games.map((game, i) => (
                                            <Link key={i} to={`/games/${game.ID}`}>
                                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:bg-gray-100 hover:border-accent transition-all">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex-1 text-right">
                                                            <span className="font-semibold">{game.team_a_name}</span>
                                                        </div>
                                                        <div className="mx-6 text-center">
                                                            <span className="text-2xl font-bold text-accent">{game.team_a_goals} - {game.team_b_goals}</span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <span className="font-semibold">{game.team_b_name}</span>
                                                        </div>
                                                    </div>
                                                    {game.text && game.text !== 'group' && (
                                                        <div className="text-center text-sm text-gray-500 mt-2">
                                                            {game.text === 'final' || game.text === 'finale' || game.text === 'finále' ? 'Finále' : 
                                                             game.text === 'o3' ? 'O 3. miesto' :
                                                             game.text === 'o5' ? 'O 5. miesto' :
                                                             game.text === 'o7' ? 'O 7. miesto' :
                                                             game.text === 'o9' ? 'O 9. miesto' :
                                                             game.text.match(/^sf\d+$/) ? 'Semifinále' :
                                                             game.text.match(/^qf\d+$/) ? 'Štvrťfinále' :
                                                             game.text}
                                                        </div>
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-4">Žiadne dokončené zápasy</p>
                                )}
                            </div>
                        )}

                        {activeTab === 'timy' && (
                            <div>
                                <h3 className="text-2xl font-semibold mb-6">Všetky tímy</h3>
                                {teams.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {teams.map((team, i) => (
                                            <Link key={i} to={`/team/${team.ID}`}>
                                                <div className="bg-gray-50 rounded-lg p-6 border border-gray-100 hover:bg-gray-100 hover:border-accent transition-all">
                                                    <h4 className="text-xl font-semibold mb-2">{team.name}</h4>
                                                    {team.group_codes && team.group_codes.length > 0 && (
                                                        <div className="flex flex-wrap gap-2">
                                                            {team.group_codes.map((codeId, j) => {
                                                                const code = codes.find(c => c.ID === codeId)
                                                                return code ? (
                                                                    <span key={j} className="text-xs bg-accent text-white px-2 py-1 rounded">
                                                                        {code.name}
                                                                    </span>
                                                                ) : null
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-4">Žiadne tímy</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    )
}
